import { IGameService, ITable } from './interfaces';
import { GameProgress, Scores } from '@base/features/game/model/interfaces';
import { MessagesService } from '@base/features/chat/messagesService';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { ISocketClient } from '@base/features/socket/interfaces';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { getRequiredEnv, t } from '@base/utils/helpers';
import { Subject, BehaviorSubject } from 'rxjs';
import { BasePreferences } from '@base/features/preferences/model/types';
import SoundProvider from './soundProvider';
import BufferReader from '../../socket/bufferReader';
import Codes from '../../socket/codes';
import Utils from '../../socket/utils';
import Decoder from '../../socket/decoder';
import BaseTablesRepository from './baseTableRepository';
import BaseTable from './baseTable';

export default class GameService<P extends BasePreferences = BasePreferences, T extends ITable = BaseTable>
  implements IGameService {
  winnerName = new Subject<string>();
  isTournamentWinnerAnimation = new BehaviorSubject(false);
  gameData = {
    reconnectionTime: 0,
  };

  constructor(
    private socket: ISocketClient,
    private tablesRepository: BaseTablesRepository<T>,
    private playersOnlineRepository: IPlayersOnlineRepository<P, T>,
    private messagesService: MessagesService<P, T>,
    private modalProvider: IModalProvider,
    private soundProvider: SoundProvider<P>
  ) {}

  sendCommand(pid: number, tid: number, cmd: number, command: string): void {
    const data = [Codes.Types.CP_GM_COMMAND]
      .concat(Utils.decToInt16(pid))
      .concat(Utils.decToInt16(tid))
      .concat(Utils.decToInt16(cmd))
      .concat(Utils.UTFtoArray(command, true, 1 ));
    this.socket.sendPacket(data);
  }

  private onReceiveGameProgress(data: BufferReader): void {
    const progress: GameProgress = {
      status: data.readInt8(),
      tid: data.readInt16(),
      turn: data.readInt8(),
      times: [],
      moves: '',
    };

    const size = data.readUint8();
    for (let i = 0; i < size; ++i) {
      progress.times.push(data.readInt32());
    }

    progress.moves = data.readUTFWithLength();
    this.onGotGameProgress(progress);
  }

  private onReceiveScommand(data: BufferReader): void {
    const type = data.readInt8();
    const command = data.readUTFWithLength();
    this.onGotStringCommand(type, command);
  }

  private checkParams(params: any, options?: any) {
    let error: any;
    error = this.tablesRepository.checkParams(params, options);
    error = this.playersOnlineRepository.checkParams(params, options);
    return error;
  }

  private onReceiveMove = (data: BufferReader) => {
    const move: {
      pid: number;
      tid: number;
      time: number;
      data?: Uint8Array;
      table?: ITable;
      player?: IPlayer;
      decoded?: string;
    } = {
      pid: data.readInt16(),
      tid: data.readInt16(),
      time: data.readInt32(),
    };

    const size = data.readInt16();
    move.data = data.read(size).toUint8Array();
    const error = this.checkParams(move, { allowPlayerId: -3, myTable: true });
    if (error) {
      return console.error('Error: gotMove', error, move);
    }

    console.info('Received move:' + JSON.stringify(move));

    move.table = this.tablesRepository.getTable(move.tid);
    move.player = move.pid !== -3 ? this.playersOnlineRepository.getPlayerOnline(move.pid) : undefined;

    if (move.player && move.player.getTable() !== move.table) {
      return console.error('Player is not playing on the table: ', move);
    }

    move.decoded = Decoder.decode(move.data);
    console.info('Decoded move:' + move.decoded);
    move.table?.getGameState().onMoveReceived(move);
  };

  private onReceiveGame = (data: BufferReader) => {
    const game: { pid: number; tid: number; cmd: number; data?: Uint8Array } = {
      pid: data.readInt16(),
      tid: data.readInt16(),
      cmd: data.readInt16(),
    };

    const size = data.readInt8();
    game.data = data.read(size).toUint8Array();

    this.onGotGame(game);
  };

  private onReceiveScore(data: BufferReader) {
    const scores: Scores = {
      type: data.readInt16(),
      tid: data.readInt16(),
      score: [],
    };

    const size = data.readInt8();
    scores.score = [];

    for (let i = 0; i < size; ++i) {
      scores.score[i] = data.readInt16();
    }

    //Only for backgammon
    if (getRequiredEnv('NX_GAME_ID') === '5') {
      this.onGotScore(scores);
    }
  }

  private onReceiveWin(data: BufferReader) {
    const action = data.readInt8();
    const pids = [data.readInt16()];
    const tid = data.readInt16();
    const reason = data.readInt16();
    this.onGotWin(action, pids, tid, reason);
  }

  private onReceiveCommand(data: BufferReader) {
    const type = data.readInt8();
    const pid = data.readInt16();
    const tid = data.readInt16();
    const par = data.readInt32();
    this.onGotCommand(type, pid, tid, par);
  }

  private onReceiveDeal(data: BufferReader): void {
    const deal = {
      type: data.readInt16(),
      tid: data.readInt16(),
      deal: data.readUTFWithLength(),
    };
    this.onGotDeal(deal);
  }

  private onReceiveWin2(data: BufferReader) {
    const winType = data.readInt8();
    const pidsCount = data.readInt8();
    const pids = [...new Array(pidsCount)].map(() => data.readInt16());
    const tid = data.readInt16();
    const reason = data.readInt16();

    this.onGotWin(winType, pids, tid, reason);
  }

  sendPlay(playCommand: any, pid?: number, tid?: number): void {
    const data = [Codes.Types.CP_PLAY]
      .concat([playCommand])
      .concat(Utils.decToInt16(pid))
      .concat(Utils.decToInt16(tid));
    this.socket.sendPacket(data);
  }

  sendMove(move: string, tid: number, time: number): void {
    const data = [Codes.Types.CP_MOVE]
      .concat(Utils.decToInt16(tid))
      .concat(Utils.decToInt32(time))
      .concat(Utils.decToInt8(move.length))
      .concat(Decoder.encode(move));
    this.socket.sendPacket(data);
  }

  sendDice(pid: number, tid: number): void {
    const data = [Codes.Types.CP_PLAY]
      .concat(Codes.PlayCommand.GET_DICE)
      .concat(Utils.decToInt16(pid))
      .concat(Utils.decToInt16(tid));
    this.socket.sendPacket(data);
  }

  get sound() {
    return this.soundProvider;
  }

  onGotStringCommand(type: number, command: string): void {
    const commandType = Codes.GameStringCommands.names[type];

    if (!commandType) {
      console.warn('Unknown game string command: ' + type);
      return;
    }

    console.info('Received game string command: ' + command);

    switch (commandType) {
      // case 'TOURNAMENT WINNER':
      //   this.me?.isWinnerAnimation()
      case 'SCORE':
        this.playersOnlineRepository.me?.getTable()?.getGameState().onScoresReceived(command);
        break;

      default:
        console.debug('Received string command ' + commandType + ': ' + command);
        break;
    }
  }

  onGotCommand = (type: number, pid: number, tid: number, par: number) => {
    const command = type;
    const him = this.playersOnlineRepository.getPlayerOnline(pid);
    const table = this.tablesRepository.getTable(tid);

    switch (command) {
      case Codes.Commands.MUTE:
        if (him != null) {
          him.setMuted(true);
          this.messagesService.broadcastChatMessage(
            him.getName() + t('muted_now') + t('type_?_for_commands')
          );
        } else {
          this.messagesService.broadcastChatMessage('room' + t('muted_now') + t('type_?_for_commands'));
        }
        break;
      case Codes.Commands.UNMUTE:
        if (him != null) {
          him.setMuted(false);
          this.messagesService.broadcastChatMessage(
            him.getName() + t('unmuted_now') + t('type_?_for_commands')
          );
        } else {
          this.messagesService.broadcastChatMessage('room' + t('unmuted_now') + t('type_?_for_commands'));
        }
        break;
      case Codes.Commands.IGNORE:
        if (him != null) {
          him.setIgnored(true);
          him.setMuted(true);
          this.messagesService.broadcastChatMessage(
            him.getName() + t('ignored_now') + t('type_?_for_commands')
          );
        }
        break;
      case Codes.Commands.UNIGNORE:
        if (him != null) {
          him.setIgnored(false);
          him.setMuted(false);
          this.messagesService.broadcastChatMessage(
            him.getName() + t('unignored_now') + t('type_?_for_commands')
          );
        }
        break;
      case Codes.Commands.HELP:
        this.messagesService.broadcastChatMessage(t('commands_mute_unmute'));
        if (this.playersOnlineRepository.me?.isAdmin() || this.playersOnlineRepository.me?.isTD()) {
          this.messagesService.broadcastChatMessage(t('adm_td_commands'));
        }
        break;
      case Codes.Commands.COLORED:
        this.messagesService.broadcastChatMessage(t('colored_now') + t('type_?_for_commands'));
        break;
      case Codes.Commands.UNCOLORED:
        this.messagesService.broadcastChatMessage(t('uncolored_now') + t('type_?_for_commands'));
        break;
      case Codes.Commands.RECONNECTION_TIME:
        this.gameData.reconnectionTime = par;
        break;
      case Codes.Commands.POSTPONE:
      case Codes.Commands.R_ENABLE:
      case Codes.Commands.R_STOP:
      case Codes.Commands.R_WAIT:
      case Codes.Commands.PM_REQ:
      case Codes.Commands.PM_WAIT:
      case Codes.Commands.PM_INFO:
        if (!table || !this.playersOnlineRepository.me?.isMyTable(table)) {
          console.error('Discarding command: ' + command + ' (wrong table' + tid + ')');
          return;
        }
        table.gameState?.onReconnectionReceieved(command, him!, par);
        break;
      case Codes.Commands.PM_URGE:
        if (!table || !this.playersOnlineRepository.me?.isMyTable(table)) {
          console.error('Discarding command: ' + command + ' (wrong table' + tid + ')');
          return;
        }
        table?.gameState?.onUrgeRecieved();
        break;
      case Codes.Commands.ALERT:
        this.sound.play(this.sound.codes.BEEP, this.sound.categories.GLOBAL);
        break;
      case Codes.Commands.START_CONFETTI:
        this.isTournamentWinnerAnimation.next(true);

        // case Codes.Commands.START_BUTTON_VISIBLE:
        // table?.gameState?.onShowStartButtonReceived(par);
        break;
      default:
        console.warn('Unknown game command: ' + type);
    }
  };

  onGotDeal(deal: any) {
    const error = this.checkParams(deal, { myTable: true });
    if (error) {
      return console.log('Error: onGotDeal', error, deal);
    }
    console.info('Received deal', deal);
    this.playersOnlineRepository.me?.getTable()?.getGameState().onDealReceived(deal);
  }

  onGotScore(scores: any): void {
    const error = this.checkParams(scores, { myTable: true });
    if (error) {
      return console.error('Error: gotScore', error, scores);
    }
    console.info('Received score:', scores);

    const table = this.tablesRepository.getTable(scores.tid);
    if (!table) throw new Error('onGotScore error: No table received.');
    table.getGameState().onScoresReceived(scores);
  }

  onGotWin(action: number, pids: number[], tid: number, reason: number): void {
    if (!this.playersOnlineRepository.me)
      throw new Error('Error on gotWin: you are not playing at this table.');
    if (tid < 0) throw new Error('Error on gotWin: invalid params: ' + tid + ', ' + JSON.stringify(pids));

    const table = this.tablesRepository.getTable(tid);
    if (!table) throw new Error("Error on gotWin: table doesn't exist: " + tid + ', ' + JSON.stringify(pids));

    if (tid !== this.playersOnlineRepository.me?.getTable()?.tid) {
      // TODO tournament
      // if (Main.isTournament() && (Main.getData().models.Player.me.isTD() || Main.getData().models.Player.me.isAdmin())) {
      //   this.sound.play(this.sound.categories.GLOBAL, this.sound.codes.COIN);
      // }
      return;
    }

    const playingPlayers = [];
    for (let i = 0; i < pids.length; ++i) {
      const pid = pids[i];
      if (pid < 0) throw new Error('Error on gotWin: invalid params: ' + tid + ', ' + JSON.stringify(pids));

      const player = this.playersOnlineRepository.getPlayerOnline(pid);
      if (!player)
        throw new Error(
          "Error on gotWin: player doesn't exist: " + tid + ', ' + pid + ', ' + JSON.stringify(pids)
        );

      if (!player.isMyTable(table))
        throw new Error('Error on gotWin: player is not playing on the table: ' + tid + ', ' + pid);
      playingPlayers.push(player);
    }

    const watchingPlayers = [];
    const allNotMe = [];
    for (let seat = 0; seat < table.getMaxPlayersCount(); seat++) {
      const player = table.getPlayerBySeat(seat);
      if (!player)
        throw new Error(
          "Error on gotWin: player doesn't exist: " + tid + ', ' + seat + ', ' + JSON.stringify(pids)
        );
      if (!this.playersOnlineRepository.findPlayer(playingPlayers, player.getPid())) {
        watchingPlayers.push(player);
      }
      if (player.getPid() !== this.playersOnlineRepository.me.getPid()) {
        allNotMe.push(player);
      }
    }

    const playersArrayToNamesString = (players: IPlayer[]) => {
      const getPlayerName = (player: IPlayer) =>
        player.getPid() === this.playersOnlineRepository.me?.getPid() ? t('you') : player.getName();

      let namesString = '';
      if (players.length === 0) {
        // do nothing
      } else if (players.length === 1) {
        namesString += getPlayerName(players[0]);
      } else {
        for (let i = 0; i < players.length - 1; ++i) {
          namesString += getPlayerName(players[i]);
          if (i !== players.length - 2) {
            namesString += ', ';
          }
        }
        namesString = t('winners_name') + namesString + getPlayerName(players[players.length - 1]);
      }
      return namesString;
    };
    const playingPlayersNames = playersArrayToNamesString(playingPlayers);
    const otherPlayersNames = playersArrayToNamesString(watchingPlayers);

    const imAmongstPlaying = !!this.playersOnlineRepository.findPlayer(
      playingPlayers,
      this.playersOnlineRepository.me.getPid()
    );
    const imAmongstWatching = !!this.playersOnlineRepository.findPlayer(
      watchingPlayers,
      this.playersOnlineRepository.me.getPid()
    );

    const winnerNames = action === Codes.WinAction.LOST ? otherPlayersNames : playingPlayersNames;
    if (winnerNames) this.winnerName.next(winnerNames);
    if (!action) console.error('Unknown win action: ' + action);
    if (!reason) console.error('Unknown win reason: ' + reason);

    let text = '';

    if (action) {
      switch (action) {
        case Codes.WinAction.WIN:
          text += playingPlayersNames;
          text += ' ' + t('won');
          text += '.';
          break;
        case Codes.WinAction.LOST:
          text += playingPlayersNames;
          text += ' ' + t('lost');
          text += '.';
          break;
        case Codes.WinAction.DRAW_OK:
          text += t('draw');
          text += '.';
          break;
      }
    }

    if (reason) {
      text += '\n';
      text += t('reason');
      text += ' ';

      switch (reason) {
        case Codes.WinReason.CHECK_TIME:
          text += t('time');
          break;
        case Codes.WinReason.CHECK_TIME_DR:
          text += t('time');
          text += '\n';
          text += t('insufficient_mating_material');
          break;
        case Codes.WinReason.STALEMATE:
          text += t('draw');
          break;
        case Codes.WinReason.RESIGN:
          text += t('resign');
          break;
        case Codes.WinReason.LEAVE:
        default:
          if (
            (action === Codes.WinAction.WIN && imAmongstWatching) ||
            (action === Codes.WinAction.LOST && imAmongstPlaying)
          ) {
            text += t('you_have_left_table');
          } else {
            text += t('opponent_left_table');
          }
          break;
      }
    }

    let isFreeWin = false;
    const imWinner =
      (action === Codes.WinAction.WIN && imAmongstPlaying) ||
      (action === Codes.WinAction.LOST && imAmongstWatching);
    if (this.playersOnlineRepository.me.isPlayingTable(table)) {
      // TODO analytics
      // Analytics.trackCountedEvent('game', table.getCost() ? 'game_complete_ticket' : 'game_complete_free', [
      //   1,
      //   10,
      //   100,
      // ]);
      if (imWinner) {
        this.winnerName.next(this.playersOnlineRepository.me.getName());
        if (table.getCost() === 0) {
          isFreeWin = true;
          text += '\n';
          text += '\n';
          text += t('congratulations_free_win');
          // TODO sounds
          // this.sound.play(this.sound.codes.COIN, this.sound.categories.GLOBAL);
        }
      }
    }

    const buttons = [
      {
        title: t('ok'),
        onClick: () => this.modalProvider.hide(),
      },
    ];

    if (imWinner) {
      // let othersNames = '';
      // let myScore = 0;
      // let othersScores = '';
      // Mahjong
      // if (getRequiredEnv('NX_GAME_ID') === '9') {
      //   othersNames = playersArrayToNamesString(allNotMe);
      //   let gameState = table.getGameState();
      //   myScore = gameState.getScore(this.me.);
      //   for (let i = 0; i < allNotMe.length; ++i) {
      //     let otherPlayer = allNotMe[i];
      //     let otherScore = otherPlayer.getName(false) + ': ' + gameState.getScore(otherPlayer);
      //     if (i !== 0) {
      //       othersScores += ', ';
      //     }
      //     othersScores += otherScore;
      //   }
      // }g
      const shareHref = document.location.href;
      buttons.push({
        title: t('share_on_facebook'),
        onClick: () => {
          (window as any).FB.ui(
            {
              display: 'popup',
              method: 'share',
              href: shareHref,
              quote: `It's A-M-A-Z-I-N-G`,
            },
            function () {
              console.log('SHAAAAAARE');
            }
          );
          // if (Game.ID === 9) {
          //   Main.facebook.shareMessage(t('mahjong_share_game', othersNames, myScore, othersScores));
          // } else {
          // TODO share
          // Main.facebook.shareGameResult(table.getGameState().getFinalScore(), otherPlayersNames);
        },
      });
    }

    if (
      table.getCost() === 0 &&
      this.playersOnlineRepository.me?.isPlayingTable(table) &&
      !this.playersOnlineRepository.me?.isTicketPlayer()
    ) {
      let title;
      let message = '';
      if (action === Codes.WinAction.DRAW_OK) {
        title = t('draw');
        message += t('better_luck_next_time');
      } else {
        title = winnerNames + ' ' + t('won') + '!';
        if (imWinner) {
          message += t('congratulations_free_win');
        } else {
          message += t('better_luck_next_time');
        }
      }

      message += '\n\n';
      message += t('get_tickets_start_earning_real_cash');
      message = message.replace('\n', '<br>');

      this.messagesService.showFreeWinMessage(buttons, message, title);
    } else if (imWinner && !isFreeWin) {
      const buttons = [
        {
          title: t('ok'),
          onClick: () => {
            this.messagesService.playWinnerAnimation();
            this.modalProvider.hide();
          },
        },
      ];
      this.messagesService.showTicketWinMessage(buttons, text, t('results_of_the_game'));
    } else if (action !== Codes.WinAction.LEAVE) {
      this.messagesService.showRegularWinMessage(buttons, text, t('results_of_the_game'), isFreeWin);
    }
  }

  onGotGameProgress(progress: GameProgress): void {
    const error = this.checkParams(progress, { myTable: true });
    if (error) {
      return console.error('Error: gotGameProgress', error, progress);
    }
    console.info('Received game progress: ', progress);
    const table = this.tablesRepository.getTable(progress.tid);
    if (!table) throw new Error('Error: gotGameProgress: no table');
    table.getGameState().loadGame(progress.status, progress.turn, progress.times, progress.moves);
  }

  onGotGame = (action: any) => {
    if (action.cmd === Codes.GameActions.REPEAT) {
      throw new Error('Game rules violated');
    }

    const error = this.checkParams(action, { allowNegativePlayerId: true });
    if (error) {
      return console.error('Error: gotGame', error, action);
    }

    const cmdName = Codes.GameActions.names[action.cmd];

    console.info('Received game action [', cmdName, ']');

    if (!cmdName) {
      return console.error('Unknown game action: ', action);
    }

    if (!this.playersOnlineRepository.me?.getTable()) {
      return console.trace('Game packet recieved for table that no longer exists', action);
    }

    // В action добавляются новые поля: player и table
    action.player =
      action.pid < 0
        ? this.playersOnlineRepository.me
        : this.playersOnlineRepository.getPlayerOnline(action.pid);
    action.table = this.playersOnlineRepository.me?.getTable();

    if (action.table.tid !== action.tid) {
      return console.error('Game action for other table', action);
    }

    if (action.player.getSeat(action.table) < 0) {
      return console.error('Game action for player, who is not at the table: ', action);
    }

    if (action.cmd === Codes.GameActions.END || action.cmd === Codes.GameActions.BREAK) {
      Decoder.setBase([-1]);
    } else if (action.cmd === Codes.GameActions.TRNID) {
      // try {
      // this.trid = action.data
      Decoder.setBase(action.data);
      // } catch (e) {
      // throw new RuntimeException();
      // }
    }

    this.playersOnlineRepository.me?.getTable()?.getGameState().onActionReceived(action);
  };
}
